import React, { useState } from 'react';
import Box from '@amzn/meridian/box';
import Button from '@amzn/meridian/button';
import Icon from '@amzn/meridian/icon';
import SearchField from '@amzn/meridian/search-field';
import Text from '@amzn/meridian/text';
import alertTokens from '@amzn/meridian-tokens/base/icon/alert';
import filterTokens from '@amzn/meridian-tokens/base/icon/filter';
import { AssignmentAssociate } from '../models';
import { getInitials } from '../../utils';

export interface AssociateSidebarProps {
  unassignedAssociates: AssignmentAssociate[];
  searchQuery: string;
  onSearchChange: (value: string) => void;
  onAssignClick: (associateId: string) => void;
}

/**
 * Left-column sidebar that lists the currently unassigned associates for
 * the Assignments page. Provides a case-insensitive name search, a filter
 * toggle, and an "Assign to..." action so the user can hand a selected
 * associate off to a workstation picker.
 *
 * Presentational: `searchQuery` and the unassigned list are owned by the
 * parent page. The sidebar only tracks which row is currently highlighted
 * (`selectedAssociateId`) and whether its filter panel is open
 * (`filterPanelOpen`). Clicking "Assign to..." invokes `onAssignClick`
 * with the currently selected associate id; the button is disabled until
 * a row is selected so callers never see a null id.
 */
const AssociateSidebar: React.FC<AssociateSidebarProps> = ({
  unassignedAssociates,
  searchQuery,
  onSearchChange,
  onAssignClick,
}) => {
  const [selectedAssociateId, setSelectedAssociateId] = useState<string | null>(
    null,
  );
  const [filterPanelOpen, setFilterPanelOpen] = useState<boolean>(false);

  const handleToggleFilterPanel = () => {
    setFilterPanelOpen((prev) => !prev);
  };

  const handleRowClick = (associateId: string) => {
    setSelectedAssociateId(associateId);
  };

  const handleAssignClick = () => {
    if (selectedAssociateId !== null) {
      onAssignClick(selectedAssociateId);
    }
  };

  return (
    <Box className="assignments-associate-sidebar" spacingInset="300">
      {/* Toolbar: search field + filter toggle */}
      <Box className="assignments-associate-sidebar__toolbar">
        <Box className="assignments-associate-sidebar__search">
          <SearchField
            value={searchQuery}
            onChange={onSearchChange}
            onSubmit={onSearchChange}
            placeholder="Search associates"
            data-testid="associate-sidebar-search"
          />
        </Box>
        <Button
          type="icon"
          onClick={handleToggleFilterPanel}
          data-testid="associate-sidebar-filter-button"
        >
          <Icon tokens={filterTokens} />
        </Button>
      </Box>

      {/* Filter panel placeholder: visible when filterPanelOpen is true. */}
      {filterPanelOpen && (
        <Box
          className="assignments-associate-sidebar__filter-panel"
          spacingInset="200"
          data-testid="associate-sidebar-filter-panel"
        >
          <Text type="b200">Filters</Text>
        </Box>
      )}

      {/* "Unassigned" header with count badge. */}
      <Box className="assignments-associate-sidebar__header">
        <Text type="h100">Unassigned</Text>
        <Box
          className="assignments-associate-sidebar__count-badge"
          data-testid="associate-sidebar-count-badge"
        >
          <Text type="b200">{unassignedAssociates.length}</Text>
        </Box>
      </Box>

      {/* Associate list. Each row is a native button so keyboard and
          screen-reader users can select it, and so TypeScript accepts the
          click handler (Meridian's `Box` props don't include `onClick`). */}
      <Box className="assignments-associate-sidebar__list">
        {unassignedAssociates.map((associate) => {
          const initials = getInitials(associate.associateName);
          const isSelected = associate.associateId === selectedAssociateId;

          return (
            <button
              key={associate.associateId}
              type="button"
              className={
                isSelected
                  ? 'assignments-associate-sidebar__row assignments-associate-sidebar__row--selected'
                  : 'assignments-associate-sidebar__row'
              }
              onClick={() => handleRowClick(associate.associateId)}
              data-testid={`associate-sidebar-row-${associate.associateId}`}
            >
              <Box className="assignments-associate-sidebar__avatar">
                <Text type="b200">{initials}</Text>
              </Box>
              <Text type="b300">{associate.associateName}</Text>
              {associate.hasWarning && (
                <Icon
                  tokens={alertTokens}
                  data-testid={`associate-sidebar-warning-${associate.associateId}`}
                />
              )}
            </button>
          );
        })}
      </Box>

      {/* Footer: "Assign to..." action. Disabled until a row is selected
          so `onAssignClick` is never invoked with a null id. */}
      <Box className="assignments-associate-sidebar__footer">
        <Button
          type="primary"
          disabled={selectedAssociateId === null}
          onClick={handleAssignClick}
          data-testid="associate-sidebar-assign-button"
        >
          Assign to...
        </Button>
      </Box>
    </Box>
  );
};

export default AssociateSidebar;
